import {
    LoadingButton as MuiLoadingButton,
    LoadingButtonProps as MuiLoadingButtonProps,
} from "@mui/lab";
import { Spinner } from "../../Feedback/Spinner/Spinner";

import styles from "./Button.module.scss";

declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides {
        primaryLink?: true;
    }
}

export interface ButtonProps
    extends Pick<
        MuiLoadingButtonProps,
        | "aria-label"
        | "children"
        | "className"
        | "color"
        | "disabled"
        | "endIcon"
        | "fullWidth"
        | "onClick"
        | "startIcon"
        | "type"
        | "variant"
    > {
    buttonRef?: React.RefObject<HTMLButtonElement>;
    isLoading?: boolean;
    size?: "extra-small" | MuiLoadingButtonProps["size"];
    variant?: "contained" | "outlined";
}

export function Button({
    "aria-label": ariaLabel,
    buttonRef,
    children,
    className = "",
    color = "primary",
    disabled,
    endIcon,
    fullWidth,
    isLoading = false,
    onClick,
    size = "medium",
    startIcon,
    type,
    variant = "contained",
}: ButtonProps) {
    let loadingPosition: MuiLoadingButtonProps["loadingPosition"] = "center";
    if (endIcon) {
        loadingPosition = "end";
    } else if (startIcon) {
        loadingPosition = "start";
    }

    return (
        <MuiLoadingButton
            aria-label={ariaLabel}
            classes={{ icon: styles["Button__icon"] }}
            className={`${styles["Button"]} ${styles[`Button--${size}`]} ${
                isLoading ? styles[`Button--loading`] : ""
            } ${variant === "outlined" ? styles[`Button--outlined`] : ""} ${
                disabled ? styles[`Button--disabled`] : ""
            } ${fullWidth ? styles["Button--fullwidth"] : ""} ${className}`}
            color={color === "primary" && variant === "outlined" ? "primaryLink" : color}
            disabled={disabled}
            endIcon={endIcon}
            fullWidth={fullWidth}
            loading={isLoading}
            loadingIndicator={
                <Spinner
                    className={styles["Button__spinner"]}
                    size={16}
                    thickness={variant === "contained" ? 5 : 3.6}
                />
            }
            loadingPosition={loadingPosition}
            onClick={onClick}
            ref={buttonRef}
            startIcon={startIcon}
            type={type}
            variant={variant}
        >
            <span className={styles["Button__content"]}>{children}</span>
        </MuiLoadingButton>
    );
}
